import { forwardRef } from 'react';

import { Divider, Flex, useTheme } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { components } from 'react-select';
import AsyncSelect from 'react-select/async';

import { Search as SearchIcon } from 'assets/assetsV1/iconComponents';

import { Avatar } from 'uiToolkitV1/Avatar';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

const SEARCH_CATEGORIES_NAME = {
  TEAM: 'Teams',
  ORGTAG: 'Org Tag',
  EMPLOYEE_NAME: 'People',
};

const ValueContainer = ({ children, ...props }) =>
  components.ValueContainer && (
    <Flex width="100%" alignItems="center" padding="8px">
      <SearchIcon boxSize="16px" style={{ marginRight: '8px' }} />
      <components.ValueContainer {...props}>{children}</components.ValueContainer>
    </Flex>
  );

const Placeholder = ({ children, ...props }) =>
  components.Placeholder && (
    <components.Placeholder {...props}>
      <TextBodyMedium>{children}</TextBodyMedium>
    </components.Placeholder>
  );

const Group = ({ children, ...props }) =>
  components.Group && (
    <components.Group {...props}>
      {children}
      <Divider orientation="horizontal" />
    </components.Group>
  );

const GroupHeading = ({ children, ...props }) => {
  const theme = useTheme();
  return (
    components.GroupHeading && (
      <components.GroupHeading style={{ textTransform: 'none', padding: '8px' }} {...props}>
        <TextLabel color={theme.colors.text.g2} bold>
          {SEARCH_CATEGORIES_NAME[children]}
        </TextLabel>
      </components.GroupHeading>
    )
  );
};

const Option = ({ children, ...props }) => {
  // const theme = useTheme();
  const { data } = props ?? {};
  const { label: name, imageUrl: profilePhotoSrc, id, email } = data ?? {};

  return (
    components.GroupHeading && (
      <components.Option {...props}>
        <Flex cursor="pointer !important" key={id} alignItems="center" height="60px" p="12px">
          <Avatar name={name} src={profilePhotoSrc} />
          <Flex ml="8px" flexDir="column">
            <TextBodyMedium ml="2px" cursor="pointer">
              {name}
            </TextBodyMedium>

            <TextLabel color="text.g2" cursor="pointer">
              {email}
            </TextLabel>
          </Flex>
        </Flex>{' '}
      </components.Option>
    )
  );
};

const TransientProps = ['defaultWidth', 'activeWidth'];

const StyledAsyncSelect = styled(AsyncSelect, {
  shouldForwardProp: (prop) => !TransientProps.includes(prop),
})(({ theme, ...props }) => ({
  display: 'block',
  '.react-select-search__control': {
    background: theme.colors.neutral.EBEFF3,
    minHeight: '32px',
    border: '1px solid',
    borderColor: 'transparent',
    borderRadius: '4px',
    width: props.defaultWidth,
    transition: 'width 200ms',
    '& svg': {
      color: theme.colors.neutral['999999'],
    },
    '& p': {
      color: theme.colors.neutral['999999'],
    },
    '&:hover': {
      borderColor: 'transparent',
      '& svg': {
        color: theme.colors.neutral['666666'],
      },
      '& p': {
        color: theme.colors.neutral['666666'],
      },
    },
    '&--is-focused': {
      background: theme.colors.common.white,
      boxShadow: theme.shadows.search,
      border: `1px solid ${theme.colors.functional['1565BC']}`,
      width: props.activeWidth,
      '& svg': {
        color: theme.colors.text.g1,
      },
      '& p': {
        color: theme.colors.text.g1,
      },
    },
    '&--is-focused:hover': {
      background: theme.colors.common.white,
      boxShadow: theme.shadows.search,
      border: `1px solid ${theme.colors.functional['1565BC']}`,
      width: props.activeWidth,
      '& svg': {
        color: theme.colors.text.g1,
      },
      '& p': {
        color: theme.colors.text.g1,
      },
    },
    '&--menu-is-open': {
      '& + .react-select-search__menu': {
        width: props.activeWidth,
      },
    },
  },
  '.react-select-search__indicators': {
    display: 'none',
  },
  '.react-select-search__group ': {},
  '.react-select-search__value-container': {
    padding: '0',
  },
  '.react-select-search__input': {
    height: '16px',
  },
  '.react-select-search__input-container': {
    padding: 0,
    margin: 0,
    height: '16px',
  },
  '.react-select-search__menu': {
    backgroundColor: theme.colors.common.white,
    boxShadow: theme.shadows.large,
    border: 'none',
    padding: '8px',
  },
  '.react-select-search__option--is-focused': {
    backgroundColor: theme.colors.functional.E6F1FD,
    cursor: 'pointer',
  },
  '.react-select-search__option': {
    padding: '8px',
    backgroundColor: theme.colors.common.white,
    '&--is-selected': {
      backgroundColor: theme.colors.functional.E6F1FD,
    },
  },
  '.react-select-search__option:hover': {
    backgroundColor: theme.colors.functional.E6F1FD,
  },
  '.react-select-search__option:active': {
    // backgroundColor: theme.colors.functional.E6F1FD
  },
}));

/**
 * @deprecated 31-03-2025
 * It was used in rabbit, and on mobile it's not used
 */
const Search = forwardRef(
  (
    {
      options = [],
      loadOptions,
      placeholder = 'Search Mesh',
      components = {},
      onChange,
      styles,
      onInputChange,
      defaultOptions,
      menuPlacement,
      defaultWidth = '180px',
      activeWidth = '320px',
      value,
    },
    ref
  ) => (
    <StyledAsyncSelect
      // options={options}
      components={{ ...components, ValueContainer, Placeholder, GroupHeading, Option, Group }}
      classNamePrefix="react-select-search"
      placeholder={placeholder}
      onChange={onChange}
      loadOptions={loadOptions}
      styles={styles}
      ref={ref}
      onInputChange={onInputChange}
      defaultOptions={defaultOptions}
      menuPlacement={menuPlacement}
      defaultWidth={defaultWidth}
      activeWidth={activeWidth}
      value={value}
    />
  )
);

export { Search };
